/**
 * Feedback API service.
 * Handles feedback submission to the backend.
 */

import { BASE_URL } from '../lib/api';

export type FeedbackType = 'Feedback' | 'Report a bug' | 'Other';

export interface SubmitFeedbackRequest {
  feedback_type: FeedbackType;
  feedback_text: string;
}

export interface SubmitFeedbackResponse {
  id: string;
  success: boolean;
}

/**
 * Submit user feedback.
 * Supports both authenticated and non-authenticated users.
 * 
 * @param feedbackType - Type of feedback (Feedback, Report a bug, Other)
 * @param feedbackText - The feedback content
 * @param token - Optional JWT authentication token (if user is authenticated)
 * @returns Promise with feedback submission response
 */
export async function submitFeedback(
  feedbackType: FeedbackType,
  feedbackText: string,
  token?: string
): Promise<SubmitFeedbackResponse> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };
  
  // Only add Authorization header if token is provided (authenticated user)
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${BASE_URL}/feedback`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify({
      feedback_type: feedbackType,
      feedback_text: feedbackText
    })
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({ error: 'Failed to submit feedback' }));
    throw new Error(errorData.error || 'Failed to submit feedback');
  }

  return res.json();
}

